import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import eventService from '../services/eventService';
import sessionService from '../services/sessionService';
import timePeriodService from '../services/timePeriodService';
import SessionAccordion from '../components/SessionAccordion';
import EarlyBirdPasswordModal from '../components/EarlyBirdPasswordModal';
import { isEarlyBirdActive, getEarlyBirdGrant } from '../utils/registration';

// Convert API time period to the shape used by helpers
const toLocalPeriod = (p) => ({
  id: parseInt(p.id, 10),
  name: p.name,
  startHour: p.start_hour,
  startMinute: p.start_minute,
  endHour: p.end_hour,
  endMinute: p.end_minute,
});

// Get hours and minutes in a specific timezone (or browser-local if no tz)
const getTimeInTimezone = (date, timezone) => {
  const d = new Date(date);
  if (!timezone) return { hours: d.getHours(), minutes: d.getMinutes() };
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (type) => parseInt(parts.find(p => p.type === type).value, 10);
  return { hours: get('hour') === 24 ? 0 : get('hour'), minutes: get('minute') };
};

// Get the time period a session belongs to based on its start time
const getSessionPeriod = (session, periods, timezone) => {
  const { hours, minutes } = getTimeInTimezone(session.start_time, timezone);
  const timeInMinutes = hours * 60 + minutes;

  for (const period of periods) {
    const periodStart = period.startHour * 60 + period.startMinute;
    const periodEnd = period.endHour * 60 + period.endMinute;
    if (timeInMinutes >= periodStart - 15 && timeInMinutes < periodEnd) {
      return period;
    }
  }
  return periods.reduce((closest, period) => {
    const periodStart = period.startHour * 60 + period.startMinute;
    const currentDiff = Math.abs(timeInMinutes - periodStart);
    const closestDiff = Math.abs(timeInMinutes - (closest.startHour * 60 + closest.startMinute));
    return currentDiff < closestDiff ? period : closest;
  });
};

// Check how many periods a session spans (based on duration)
const getSessionPeriodSpan = (session) => {
  const startTime = new Date(session.start_time);
  const endTime = new Date(session.end_time);
  const durationMinutes = (endTime - startTime) / (1000 * 60);
  if (durationMinutes > 90) {
    return 2;
  }
  return 1;
};

// Get all periods a session occupies
const getOccupiedPeriods = (session, periods, timezone) => {
  const startPeriod = getSessionPeriod(session, periods, timezone);
  const span = getSessionPeriodSpan(session);

  if (span === 1) return [startPeriod.id];

  const startIndex = periods.findIndex(p => p.id === startPeriod.id);
  const occupiedIds = [];
  for (let i = 0; i < span && startIndex + i < periods.length; i++) {
    occupiedIds.push(periods[startIndex + i].id);
  }
  return occupiedIds;
};

// Format period time range for display
const formatPeriodTime = (period) => {
  const formatHour = (hour, minute) => {
    const h = hour > 12 ? hour - 12 : hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${h}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };
  return `${formatHour(period.startHour, period.startMinute)} - ${formatHour(period.endHour, period.endMinute)}`;
};

function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const toast = useToast();
  const [event, setEvent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [dates, setDates] = useState([]);
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedDate, setSelectedDate] = useState('all');
  const [isRegistered, setIsRegistered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registering, setRegistering] = useState(false);

  // Registration wizard state
  const [registrationStatus, setRegistrationStatus] = useState(null);

  // Early bird password modal state
  const [showEarlyBirdModal, setShowEarlyBirdModal] = useState(false);

  // Time periods from API
  const [timePeriods, setTimePeriods] = useState([]);

  // Schedule management state
  const [userSchedule, setUserSchedule] = useState([]);
  const [schedulingSession, setSchedulingSession] = useState(null);
  const [conflictWarning, setConflictWarning] = useState(null);

  // Session detail modal state
  const [detailSession, setDetailSession] = useState(null);

  // Sticky bar state
  const [showStickyBar, setShowStickyBar] = useState(false);
  const heroRef = useRef(null);

  // IntersectionObserver for sticky bar
  useEffect(() => {
    const heroEl = heroRef.current;
    if (!heroEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyBar(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(heroEl);
    return () => observer.disconnect();
  }, [loading]);

  useEffect(() => {
    if (!authLoading) {
      fetchEventData();
    }
  }, [slug, authLoading, user?.id]);

  // Handle registrationComplete/registrationUpdated navigation state from wizard
  useEffect(() => {
    if (location.state?.registrationComplete) {
      toast.success('Registration complete! Your registration has been locked.', 5000);
      // Clear the state so it doesn't re-trigger on refresh
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.registrationUpdated) {
      toast.success('Registration updated and locked.', 5000);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.registrationComplete, location.state?.registrationUpdated]);

  const fetchEventData = async () => {
    try {
      setLoading(true);
      setError(null);

      const eventData = await eventService.getEventBySlug(slug);
      setEvent(eventData.event);

      // Fetch sessions, dates, and time periods
      const [sessionsData, datesData, periodsRes] = await Promise.all([
        sessionService.getEventSessions(eventData.event.id, { isPublished: true }),
        sessionService.getScheduleDates(eventData.event.id),
        timePeriodService.getTimePeriods(eventData.event.id).catch(() => ({ data: { timePeriods: [] } })),
      ]);

      setSessions(sessionsData.sessions);
      setDates(datesData.dates);
      const rawPeriods = periodsRes.data?.timePeriods || [];
      setTimePeriods(rawPeriods.map(toLocalPeriod));

      // Check if user is registered and fetch their schedule
      if (user) {
        const userEvents = await eventService.getUserRegisteredEvents();
        const registered = userEvents.events.some((e) => e.id === eventData.event.id);
        setIsRegistered(registered);

        // Fetch registration status (wizard lock state)
        if (registered) {
          try {
            const regStatusRes = await eventService.getRegistrationStatus(eventData.event.id);
            setRegistrationStatus(regStatusRes.data);
          } catch (regErr) {
            console.error('Failed to fetch registration status:', regErr);
          }
        }

        // Fetch user's schedule if registered
        if (registered) {
          try {
            const scheduleData = await sessionService.getUserSchedule(eventData.event.id);
            setUserSchedule(scheduleData.sessions || []);
          } catch (scheduleErr) {
            console.error('Failed to fetch schedule:', scheduleErr);
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = () => {
    if (!user) {
      navigate('/login', { state: { from: `/events/${slug}/detail` } });
      return;
    }

    // During the early bird window, require password verification first
    if (isEarlyBirdActive(event) && !getEarlyBirdGrant(event.id)) {
      setShowEarlyBirdModal(true);
      return;
    }

    // Navigate to the registration wizard
    navigate(`/events/${event.id}/register`);
  };

  const handleUnregister = async () => {
    try {
      setRegistering(true);
      await eventService.unregisterFromEvent(event.id);
      setIsRegistered(false);
      setUserSchedule([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to unregister');
    } finally {
      setRegistering(false);
    }
  };

  // Check if a session is in the user's schedule
  const isInSchedule = (sessionId) => {
    return userSchedule.some((s) => s.id === sessionId && !s.not_attending);
  };

  // Check for time conflicts with existing scheduled sessions
  const checkTimeConflicts = (session) => {
    const sessionStart = new Date(session.start_time).getTime();
    const sessionEnd = new Date(session.end_time).getTime();

    const conflicts = userSchedule.filter((scheduled) => {
      if (scheduled.not_attending) return false;
      const scheduledStart = new Date(scheduled.start_time).getTime();
      const scheduledEnd = new Date(scheduled.end_time).getTime();

      // Check for overlap
      return sessionStart < scheduledEnd && sessionEnd > scheduledStart;
    });

    return conflicts;
  };

  // Handle adding/removing session from schedule
  const handleScheduleToggle = async (e, session, onCollapse) => {
    e.preventDefault(); // Prevent navigation to session detail
    e.stopPropagation();

    if (!user) {
      navigate('/login', { state: { from: `/events/${slug}/detail` } });
      return;
    }

    if (!isRegistered) {
      setError('Please register for the event first to add sessions to your schedule.');
      return;
    }

    const inSchedule = isInSchedule(session.id);

    if (!inSchedule) {
      // Check for conflicts before adding - only allow one session per time slot
      const conflicts = checkTimeConflicts(session);
      if (conflicts.length > 0) {
        setConflictWarning({ session, conflicts });
        return;
      }
    }

    await toggleScheduleSession(session, inSchedule, onCollapse);
  };

  // Actually perform the schedule toggle
  const toggleScheduleSession = async (session, isRemoving, onCollapse) => {
    try {
      setSchedulingSession(session.id);
      setError(null);

      if (isRemoving) {
        await sessionService.removeFromSchedule(session.id);
        setUserSchedule((prev) => prev.filter((s) => s.id !== session.id));
        toast.info(`"${session.title}" removed from your schedule`, 3000);
      } else {
        await sessionService.addToSchedule(session.id);
        setUserSchedule((prev) => [...prev, session]);
        toast.success(`"${session.title}" added to your schedule`, 3000);
        // Collapse the accordion after adding a session
        if (onCollapse) {
          onCollapse();
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || `Failed to ${isRemoving ? 'remove from' : 'add to'} schedule`;
      setError(errorMsg);
      toast.error(errorMsg, 4000);
    } finally {
      setSchedulingSession(null);
    }
  };

  // Close the conflict warning modal
  const closeConflictWarning = () => {
    setConflictWarning(null);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (datetime) => {
    return new Date(datetime).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDateShort = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Registration deadline helpers
  const getDeadlineInfo = useCallback(() => {
    if (!event?.registration_closes_at || event.registration_status !== 'open') return null;
    const closes = new Date(event.registration_closes_at);
    const now = new Date();
    const hoursLeft = (closes - now) / (1000 * 60 * 60);
    if (hoursLeft <= 0) return null;

    const daysLeft = hoursLeft / 24;
    const isUrgent = hoursLeft <= 48;
    const isClosingSoon = daysLeft <= 7;

    let text;
    if (hoursLeft <= 24) {
      text = 'Registration closes today';
    } else if (hoursLeft <= 48) {
      text = 'Registration closes tomorrow';
    } else {
      text = `Registration closes ${closes.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }

    return { text, isUrgent, isClosingSoon };
  }, [event?.registration_closes_at, event?.registration_status]);

  // Derive unique tags from sessions for filter dropdown
  const uniqueTags = useMemo(() =>
    [...new Map(sessions.flatMap(s => s.tags || []).map(t => [t.id, t])).values()],
    [sessions]
  );

  // Filter sessions
  const filteredSessions = sessions.filter((session) => {
    if (selectedTag !== 'all' && !session.tags?.some(t => String(t.id) === selectedTag)) return false;
    if (selectedDate !== 'all') {
      const sessionDate = new Date(session.start_time).toISOString().split('T')[0];
      if (sessionDate !== selectedDate) return false;
    }
    return true;
  });

  // Group sessions by date, then by period (or by time slot when no periods defined)
  const sessionsByDateAndPeriod = filteredSessions.reduce((acc, session) => {
    const date = new Date(session.start_time).toISOString().split('T')[0];

    if (timePeriods.length === 0) {
      // No time periods: group by start time as a synthetic period
      const tz = event?.timezone;
      const { hours: h, minutes: m } = getTimeInTimezone(session.start_time, tz);
      const timeKey = `${h}:${String(m).padStart(2, '0')}`;
      const { hours: eh, minutes: em } = getTimeInTimezone(session.end_time, tz);

      if (!acc[date]) acc[date] = {};
      if (!acc[date][timeKey]) {
        acc[date][timeKey] = {
          period: {
            id: timeKey,
            name: `${formatTime(session.start_time)} - ${formatTime(session.end_time)}`,
            startHour: h, startMinute: m,
            endHour: eh, endMinute: em,
          },
          sessions: [],
        };
      }
      acc[date][timeKey].sessions.push(session);
      return acc;
    }

    const period = getSessionPeriod(session, timePeriods, event?.timezone);

    if (!acc[date]) acc[date] = {};
    if (!acc[date][period.id]) acc[date][period.id] = { period, sessions: [] };
    acc[date][period.id].sessions.push(session);
    return acc;
  }, {});

  // Sort sessions by title within each period (they all start at same time)
  Object.keys(sessionsByDateAndPeriod).forEach((date) => {
    Object.keys(sessionsByDateAndPeriod[date]).forEach((periodId) => {
      sessionsByDateAndPeriod[date][periodId].sessions.sort(
        (a, b) => a.title.localeCompare(b.title)
      );
    });
  });

  // Check if a period is blocked by a multi-session in user's schedule
  const isPeriodBlocked = (date, periodId) => {
    return userSchedule.some(scheduled => {
      const scheduledDate = new Date(scheduled.start_time).toISOString().split('T')[0];
      if (scheduledDate !== date) return false;

      const occupiedPeriods = getOccupiedPeriods(scheduled, timePeriods, event?.timezone);
      const startPeriod = getSessionPeriod(scheduled, timePeriods, event?.timezone);

      return occupiedPeriods.includes(parseInt(periodId)) && startPeriod.id !== parseInt(periodId);
    });
  };

  // Get the session that's blocking a period
  const getBlockingSession = (date, periodId) => {
    return userSchedule.find(scheduled => {
      const scheduledDate = new Date(scheduled.start_time).toISOString().split('T')[0];
      if (scheduledDate !== date) return false;

      const occupiedPeriods = getOccupiedPeriods(scheduled, timePeriods, event?.timezone);
      const startPeriod = getSessionPeriod(scheduled, timePeriods, event?.timezone);

      return occupiedPeriods.includes(parseInt(periodId)) && startPeriod.id !== parseInt(periodId);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900" />
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl text-stone-900 mb-2">Event Not Found</h1>
          <p className="text-stone-600 mb-4">{error}</p>
          <Link
            to="/events"
            className="text-amber-600 hover:text-amber-700 font-medium"
          >
            Browse all events
          </Link>
        </div>
      </div>
    );
  }

  const isOwner = user && event.creator?.id === user.id;

  // Early bird window grants registration access independently of registration_status.
  // canRegister is true when standard registration is open OR the early bird window is active.
  const earlyBirdActive = isEarlyBirdActive(event);
  const canRegister = event.registration_status === 'open' || earlyBirdActive;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <header
        ref={heroRef}
        className="relative"
        style={{ backgroundColor: event.brand_color || '#1c1917' }}
      >
        {event.banner_url && (
          <img
            src={event.banner_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
        )}
        <div className="relative max-w-6xl mx-auto px-4 py-16 text-white">
          <div className="flex items-center gap-4 mb-6">
            <Link
              to={`/events/${slug}`}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Event
            </Link>
            <span className="text-white/30">|</span>
            <Link
              to="/events"
              className="text-white/70 hover:text-white transition-colors"
            >
              All Events
            </Link>
          </div>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div>
              <h1 className="font-display text-4xl md:text-5xl mb-4 tracking-tight">
                {event.name}
              </h1>

              <div className="space-y-2 text-white/80">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {formatDate(event.start_date)}
                  {event.start_date !== event.end_date && ` - ${formatDate(event.end_date)}`}
                </div>

                {event.location && (
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {event.venue_name && <span>{event.venue_name}, </span>}
                    {event.location}
                  </div>
                )}

                {event.is_virtual && event.virtual_url && (
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Virtual Event
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons - owner, registered, closed, paused (non-register states) */}
            {(isOwner || isRegistered || (!canRegister && (event.registration_status === 'closed' || event.registration_status === 'paused'))) && (
              <div className="flex flex-col gap-3">
                {isOwner ? (
                  <Link
                    to={`/events/${event.id}/manage`}
                    className="px-6 py-3 border border-white/40 text-white font-medium rounded-lg hover:bg-white/10 transition-colors text-center"
                  >
                    Manage Event
                  </Link>
                ) : isRegistered ? (
                  <Link
                    to={`/events/${event.slug}/schedule`}
                    className="group px-6 py-3 bg-amber-500 text-stone-900 font-semibold rounded-lg hover:bg-amber-400 transition-all text-center flex items-center justify-center gap-2 shadow-lg shadow-amber-500/30"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    View My Schedule
                    {userSchedule.length > 0 && (
                      <span className="ml-1 px-2 py-0.5 bg-stone-900/20 rounded-full text-sm">
                        {userSchedule.length}
                      </span>
                    )}
                  </Link>
                ) : event.registration_status === 'closed' ? (
                  <div className="px-6 py-3 bg-stone-600 text-stone-300 font-medium rounded-lg text-center cursor-not-allowed">
                    Registration Closed
                  </div>
                ) : event.registration_status === 'paused' ? (
                  <div>
                    <div className="px-6 py-3 bg-amber-600/30 text-amber-200 font-medium rounded-lg text-center border border-amber-500/40">
                      Registration Paused
                    </div>
                    {event.registration_paused_message && (
                      <p className="mt-2 text-sm text-amber-200/70 text-center">
                        {event.registration_paused_message}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Registration CTA - full-width banner when registration is open (or early bird active) and user not registered/owner */}
          {!isOwner && !isRegistered && canRegister && (
            <div className="mt-8 pt-6 border-t border-white/15">
              {/* Early bird info banner */}
              {earlyBirdActive && (
                <div className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/20 border border-amber-400/30">
                  <svg className="w-5 h-5 text-amber-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-amber-100">
                    Early bird registration is open until {formatDate(event.early_bird_closes_at)}. Password required.
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <button
                  onClick={handleRegister}
                  className="px-8 py-4 bg-amber-500 text-stone-900 text-lg font-semibold rounded-lg hover:bg-amber-400 transition-all shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40 flex items-center gap-2 whitespace-nowrap"
                >
                  Register Now
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div className="text-center sm:text-left">
                  <p className="text-white/80 text-sm">
                    Browse the sessions below, then register to build your personal schedule.
                  </p>
                  {(() => {
                    const deadline = getDeadlineInfo();
                    if (!deadline) return null;
                    return (
                      <p className={`text-sm mt-1 ${deadline.isUrgent ? 'text-amber-300 font-medium' : 'text-white/50'}`}>
                        {deadline.text}
                      </p>
                    );
                  })()}
                </div>
              </div>
              {/* Upcoming early bird subnote — show when early bird is enabled but not yet open */}
              {!earlyBirdActive && event.early_bird_enabled && event.early_bird_opens_at && new Date() < new Date(event.early_bird_opens_at) && (
                <p className="mt-3 text-xs text-white/50">
                  Early bird registration opens {formatDate(event.early_bird_opens_at)}
                </p>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4 text-sm">
            <div>
              <span className="text-white/60">Attendees</span>
              <p className="text-2xl font-display">{event.attendee_count || 0}</p>
            </div>
            <div>
              <span className="text-white/60">Sessions</span>
              <p className="text-2xl font-display">{sessions.length}</p>
            </div>
            {(() => {
              const uniqueTags = [...new Map(sessions.flatMap(s => s.tags || []).map(t => [t.id, t])).values()];
              return uniqueTags.length > 0 ? (
                <div>
                  <span className="text-white/60">Tags</span>
                  <p className="text-2xl font-display">{uniqueTags.length}</p>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      </header>

      {/* Sticky Registration Bar */}
      {showStickyBar && (
        <div
          className="fixed top-0 left-0 right-0 z-50 transition-transform duration-300"
          style={{
            backgroundColor: event.brand_color ? `${event.brand_color}ee` : '#1c1917ee',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            animation: 'slideDown 0.3s ease-out',
          }}
        >
          <style>{`
            @keyframes slideDown {
              from { transform: translateY(-100%); }
              to { transform: translateY(0); }
            }
          `}</style>
          <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
            <h2 className="text-white font-semibold truncate text-sm sm:text-base">
              {event.name}
            </h2>
            <div className="flex items-center gap-3 flex-shrink-0">
              {(() => {
                const deadline = getDeadlineInfo();
                if (deadline?.isClosingSoon) {
                  return (
                    <span className={`hidden sm:block text-xs ${deadline.isUrgent ? 'text-amber-300 font-medium' : 'text-white/60'}`}>
                      {deadline.text}
                    </span>
                  );
                }
                return null;
              })()}
              {isOwner ? (
                <Link
                  to={`/events/${event.id}/manage`}
                  className="px-4 py-2 border border-white/40 text-white text-sm font-medium rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap"
                >
                  Manage Event
                </Link>
              ) : isRegistered ? (
                <Link
                  to={`/events/${event.slug}/schedule`}
                  className="px-4 py-2 bg-amber-500 text-stone-900 text-sm font-semibold rounded-lg hover:bg-amber-400 transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  My Schedule
                  {userSchedule.length > 0 && (
                    <span className="px-1.5 py-0.5 bg-stone-900/20 rounded-full text-xs font-bold">
                      {userSchedule.length}
                    </span>
                  )}
                </Link>
              ) : !canRegister && event.registration_status === 'closed' ? (
                <div className="px-4 py-2 bg-stone-600 text-stone-300 text-sm font-medium rounded-lg cursor-not-allowed whitespace-nowrap">
                  Registration Closed
                </div>
              ) : !canRegister && event.registration_status === 'paused' ? (
                <div className="px-4 py-2 bg-amber-600/30 text-amber-200 text-sm font-medium rounded-lg border border-amber-500/40 whitespace-nowrap">
                  Registration Paused
                </div>
              ) : canRegister ? (
                <button
                  onClick={handleRegister}
                  className="px-4 py-2 bg-amber-500 text-stone-900 text-sm font-semibold rounded-lg hover:bg-amber-400 transition-colors flex items-center gap-1.5 whitespace-nowrap"
                >
                  Register Now
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="max-w-6xl mx-auto px-4 mt-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Sessions */}
          <div className="lg:col-span-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="font-display text-2xl text-stone-900">Session List</h2>
                {!isOwner && !isRegistered && canRegister && sessions.length > 0 && (
                  <p className="text-sm text-stone-500 mt-1">
                    Browse the sessions below, then click <strong className="text-amber-600">Register Now</strong> to build your personal schedule.
                  </p>
                )}
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                {dates.length > 1 && (
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-3 py-2 border border-stone-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="all">All Dates</option>
                    {dates.map((date) => (
                      <option key={date} value={date}>
                        {formatDateShort(date)}
                      </option>
                    ))}
                  </select>
                )}

                {uniqueTags.length > 0 && (
                  <select
                    value={selectedTag}
                    onChange={(e) => setSelectedTag(e.target.value)}
                    className="px-3 py-2 border border-stone-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="all">All Tags</option>
                    <optgroup label="Licensure">
                      {uniqueTags.filter(t => t.type === 'licensure').map((t) => (
                        <option key={t.id} value={String(t.id)}>
                          {t.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Audience">
                      {uniqueTags.filter(t => t.type === 'audience').map((t) => (
                        <option key={t.id} value={String(t.id)}>
                          {t.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                )}
              </div>
            </div>

            {/* View All Presenters link */}
            {sessions.some((s) => s.speakers?.length > 0) && (
              <div className="mb-6">
                <Link
                  to={`/events/${event.slug}/presenters`}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-white border border-stone-200 rounded-lg hover:bg-stone-50 hover:text-stone-900 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  View All Presenters
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            )}

            {/* Session List */}
            {Object.keys(sessionsByDateAndPeriod).length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
                <p className="text-stone-500">No sessions scheduled yet</p>
              </div>
            ) : (
              <div className="space-y-8">
                {Object.keys(sessionsByDateAndPeriod)
                  .sort()
                  .map((date) => (
                    <div key={date}>
                      <h3 className="font-display text-lg text-stone-700 mb-4">
                        {formatDate(date)}
                      </h3>

                      <div className="space-y-4">
                        {(timePeriods.length > 0
                          ? timePeriods.map(p => ({ key: p.id, periodData: sessionsByDateAndPeriod[date][p.id], period: p }))
                          : Object.keys(sessionsByDateAndPeriod[date]).sort().map(key => ({ key, periodData: sessionsByDateAndPeriod[date][key], period: sessionsByDateAndPeriod[date][key].period }))
                        ).map(({ key, periodData, period }) => {
                          const isBlocked = timePeriods.length > 0 ? isPeriodBlocked(date, period.id) : false;
                          const blockingSession = isBlocked ? getBlockingSession(date, period.id) : null;

                          // Skip periods with no sessions and not blocked
                          if (!periodData && !isBlocked) return null;

                          // Check if user has a registered session for this period
                          const registeredSession = userSchedule.find(scheduled => {
                            if (scheduled.not_attending) return false;
                            const scheduledDate = new Date(scheduled.start_time).toISOString().split('T')[0];
                            if (scheduledDate !== date) return false;
                            if (timePeriods.length === 0) {
                              const st = new Date(scheduled.start_time);
                              const timeKey = `${st.getHours()}:${String(st.getMinutes()).padStart(2, '0')}`;
                              return timeKey === key;
                            }
                            const scheduledPeriod = getSessionPeriod(scheduled, timePeriods, event?.timezone);
                            return scheduledPeriod.id === period.id;
                          });

                          // Check if user marked this period as "not attending"
                          const isNotAttending = userSchedule.some(scheduled => {
                            if (!scheduled.not_attending) return false;
                            const scheduledDate = new Date(scheduled.start_time).toISOString().split('T')[0];
                            if (scheduledDate !== date) return false;
                            if (timePeriods.length === 0) {
                              const st = new Date(scheduled.start_time);
                              const timeKey = `${st.getHours()}:${String(st.getMinutes()).padStart(2, '0')}`;
                              return timeKey === key;
                            }
                            const scheduledPeriod = getSessionPeriod(scheduled, timePeriods, event?.timezone);
                            return scheduledPeriod.id === period.id;
                          });

                          // Blocked content component
                          const blockedContent = isBlocked && blockingSession ? (
                            <div
                              onClick={() => setDetailSession(blockingSession)}
                              className="block bg-amber-50 border border-amber-200 rounded-xl p-4 hover:bg-amber-100 transition-colors cursor-pointer"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-amber-900">{blockingSession.title}</p>
                                  <p className="text-amber-600 text-sm">Continues from {getSessionPeriod(blockingSession, timePeriods, event?.timezone).name}</p>
                                </div>
                                <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700 font-medium">
                                  2 Sessions
                                </span>
                              </div>
                            </div>
                          ) : null;

                          // Render individual session card
                          const renderSessionCard = (session, requestCollapse) => {
                                    const scheduled = isInSchedule(session.id);
                                    const isScheduling = schedulingSession === session.id;
                                    const sessionSpan = getSessionPeriodSpan(session);
                                    const isPresenter = user?.email && session.speakers?.some(
                                      (sp) => sp.speaker_email && sp.speaker_email.toLowerCase() === user.email.toLowerCase()
                                    );

                          return (
                            <div
                              key={session.id}
                              onClick={() => setDetailSession(session)}
                              className={`block rounded-xl border p-4 sm:p-5 hover:shadow-md transition-all group relative cursor-pointer ${
                                isPresenter
                                  ? 'bg-emerald-50 border-emerald-300 hover:border-emerald-400 ring-1 ring-emerald-200'
                                  : scheduled
                                  ? 'bg-amber-50 border-amber-300 hover:border-amber-400'
                                  : 'bg-white border-stone-200 hover:border-stone-300'
                              }`}
                            >
                              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                                {/* Time */}
                                <div className="flex sm:flex-col sm:w-24 flex-shrink-0 text-sm text-stone-500 gap-2 sm:gap-0">
                                  <span>{formatTime(session.start_time)}</span>
                                  <span className="text-stone-300 sm:hidden">-</span>
                                  <span className="sm:block text-stone-400">
                                    {formatTime(session.end_time)}
                                  </span>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <h4 className={`font-medium group-hover:text-amber-600 transition-colors ${
                                          isPresenter ? 'text-emerald-900' : scheduled ? 'text-amber-900' : 'text-stone-900'
                                        }`}>
                                          {session.title}
                                        </h4>
                                        {isPresenter && (
                                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-full bg-emerald-600 text-white font-semibold">
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            Presenter
                                          </span>
                                        )}
                                      </div>

                                      {session.speakers?.length > 0 && (
                                        <p className="text-sm text-stone-500 mt-1">
                                          {session.speakers
                                            .map((s) => s.speaker_name)
                                            .join(', ')}
                                        </p>
                                      )}
                                    </div>

                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {/* Extended session badge */}
                                      {sessionSpan > 1 && (
                                        <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700 font-medium">
                                          2 Sessions
                                        </span>
                                      )}

                                      {/* Tag badges */}
                                      {session.tags && session.tags.length > 0 ? (
                                        session.tags.map(tag => (
                                          <span
                                            key={tag.id}
                                            className="px-2 py-1 text-xs rounded-full"
                                            style={{
                                              backgroundColor: tag.type === 'licensure' && tag.color ? `${tag.color}20` : '#f5f5f4',
                                              color: tag.type === 'licensure' && tag.color ? tag.color : '#78716c',
                                              marginRight: '0.25rem',
                                            }}
                                          >
                                            {tag.name}
                                          </span>
                                        ))
                                      ) : session.track ? (
                                        <span
                                          className="px-2 py-1 text-xs rounded-full"
                                          style={{
                                            backgroundColor: session.track_color
                                              ? `${session.track_color}20`
                                              : '#f5f5f4',
                                            color: session.track_color || '#78716c',
                                          }}
                                        >
                                          {session.track}
                                        </span>
                                      ) : null}

                                      {/* Schedule status indicator (read-only, registration handled via wizard) */}
                                      {user && isRegistered && scheduled && (
                                        <div className="p-2 rounded-lg bg-amber-500 text-white" title="In your schedule">
                                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Meta */}
                                  <div className="mt-3 flex flex-wrap gap-3 text-xs text-stone-400">
                                    {scheduled && (
                                      <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        In your schedule
                                      </span>
                                    )}

                                    {(session.room_name || session.room) && (
                                      <span className="inline-flex items-center gap-1">
                                        <svg
                                          className="w-3 h-3"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"
                                          />
                                        </svg>
                                        {session.room_name
                                          ? (session.room_building ? `${session.room_building} - ${session.room_name}` : session.room_name)
                                          : session.room}
                                      </span>
                                    )}

                                    <span className="capitalize">
                                      {session.session_type.replace('_', ' ')}
                                    </span>

                                    {session.capacity && (
                                      <span>
                                        {session.current_attendees}/{session.capacity} seats
                                      </span>
                                    )}
                                  </div>

                                  {/* View Details button */}
                                  <div className="mt-3 flex justify-end">
                                    <button
                                      onClick={() => setDetailSession(session)}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                    >
                                      View Details
                                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                          };

                          // Check if the user is presenting any session in this period
                          const presenterSession = user?.email && periodData?.sessions?.find(s =>
                            s.speakers?.some(sp => sp.speaker_email && sp.speaker_email.toLowerCase() === user.email.toLowerCase())
                          );

                          // For the header: presenter session takes priority, then registered, then blocking extended session
                          const effectiveRegistered = presenterSession || registeredSession || (isBlocked && blockingSession && isInSchedule(blockingSession.id) ? blockingSession : null);
                          const effectiveIsPresenter = !!presenterSession;

                          return (
                            <SessionAccordion
                              key={key}
                              period={period}
                              sessions={periodData?.sessions || []}
                              blockedContent={blockedContent}
                              renderSession={renderSessionCard}
                              formatPeriodTime={formatPeriodTime}
                              defaultExpanded={false}
                              brandColor={event.brand_color || '#1c1917'}
                              hasRegisteredSession={!!effectiveRegistered}
                              registeredSessionTitle={effectiveRegistered?.title || ''}
                              isPresenterSession={!!effectiveIsPresenter}
                              isNotAttending={isNotAttending}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {/* Bottom Registration CTA */}
            {!isOwner && !isRegistered && canRegister && sessions.length > 0 && (
              <div className="mt-8 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-6 text-center">
                <h3 className="font-display text-xl text-stone-900 mb-2">
                  Ready to join?
                </h3>
                <p className="text-stone-600 text-sm mb-4">
                  Register now to select your sessions and build your personal schedule.
                </p>
                <button
                  onClick={handleRegister}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-stone-900 font-semibold rounded-lg hover:bg-amber-400 transition-all shadow-md shadow-amber-500/20 hover:shadow-lg hover:shadow-amber-500/30"
                >
                  Register Now
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                {(() => {
                  const deadline = getDeadlineInfo();
                  if (!deadline) return null;
                  return (
                    <p className={`mt-3 text-xs ${deadline.isUrgent ? 'text-amber-700 font-medium' : 'text-stone-500'}`}>
                      {deadline.text}
                    </p>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* About */}
            <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
              <h3 className="font-display text-lg text-stone-900 mb-4">About</h3>
              {event.description ? (
                <p className="text-stone-600 whitespace-pre-wrap">
                  {event.description}
                </p>
              ) : (
                <p className="text-stone-400 italic">No description provided</p>
              )}
            </div>

            {/* Venue */}
            {(event.venue_name || event.venue_address) && (
              <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
                <h3 className="font-display text-lg text-stone-900 mb-4">Venue</h3>
                {event.venue_name && (
                  <p className="font-medium text-stone-800">{event.venue_name}</p>
                )}
                {event.venue_address && (
                  <p className="text-stone-600 mt-1">{event.venue_address}</p>
                )}
              </div>
            )}

            {/* Organizer */}
            {event.creator && (
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="font-display text-lg text-stone-900 mb-4">Organizer</h3>
                <p className="text-stone-700">
                  {event.creator.first_name} {event.creator.last_name}
                </p>
                <p className="text-stone-500 text-sm">{event.creator.email}</p>
              </div>
            )}

            {/* Registration Status Card - for registered users */}
            {user && isRegistered && (
              <div className="mt-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-stone-900">You're Registered!</p>
                      {registrationStatus?.registration_locked && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          Locked
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-stone-600">
                      {userSchedule.length} session{userSchedule.length !== 1 ? 's' : ''} in your schedule
                    </p>
                  </div>
                </div>

                <Link
                  to={`/events/${event.slug}/schedule`}
                  className="block w-full px-4 py-2.5 bg-amber-500 text-stone-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors text-center mb-3"
                >
                  View My Schedule
                </Link>

                {/* Wizard-registered and locked: disable unregister, show tooltip */}
                {registrationStatus?.wizard_completed_at && registrationStatus?.registration_locked ? (
                  <div className="relative group">
                    <button
                      disabled
                      className="w-full px-4 py-2 text-stone-400 rounded-lg text-sm font-medium cursor-not-allowed"
                    >
                      Unregister from Event
                    </button>
                    <div className="invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-stone-800 text-white text-xs rounded-lg whitespace-nowrap shadow-lg">
                      Contact organizer to unlock your registration first
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-stone-800" />
                    </div>
                  </div>
                ) : registrationStatus?.wizard_completed_at && !registrationStatus?.registration_locked ? (
                  /* Wizard-registered but unlocked: show Edit Registration */
                  <>
                    <button
                      onClick={() => navigate(`/events/${event.id}/register`)}
                      className="w-full px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors text-sm font-medium mb-2"
                    >
                      Edit Registration
                    </button>
                    <button
                      onClick={handleUnregister}
                      disabled={registering}
                      className="w-full px-4 py-2 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                    >
                      {registering ? 'Processing...' : 'Unregister from Event'}
                    </button>
                  </>
                ) : (
                  /* Old registration (no wizard_completed_at): normal unregister */
                  <button
                    onClick={handleUnregister}
                    disabled={registering}
                    className="w-full px-4 py-2 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                  >
                    {registering ? 'Processing...' : 'Unregister from Event'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Floating Schedule Button - visible when scrolled and registered */}
      {user && isRegistered && (
        <Link
          to={`/events/${event.slug}/schedule`}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 bg-stone-900 text-white font-semibold rounded-full shadow-2xl hover:bg-stone-800 transition-all hover:scale-105 group"
        >
          <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <span>My Schedule</span>
          {userSchedule.length > 0 && (
            <span className="px-2 py-0.5 bg-amber-500 text-stone-900 rounded-full text-sm font-bold">
              {userSchedule.length}
            </span>
          )}
        </Link>
      )}

      {/* Conflict Warning Modal */}
      {conflictWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-stone-900 mb-2">Schedule Conflict</h3>
                <p className="text-stone-600 mb-4">
                  You cannot add "{conflictWarning.session.title}" because it overlaps with {conflictWarning.conflicts.length === 1 ? 'a session' : `${conflictWarning.conflicts.length} sessions`} already in your schedule:
                </p>

                <div className="space-y-2 mb-4">
                  {conflictWarning.conflicts.map((conflict) => (
                    <div
                      key={conflict.id}
                      className="p-3 bg-red-50 rounded-lg border border-red-200"
                    >
                      <p className="font-medium text-stone-900 text-sm">{conflict.title}</p>
                      <p className="text-xs text-stone-500 mt-1">
                        {formatTime(conflict.start_time)} - {formatTime(conflict.end_time)}
                      </p>
                    </div>
                  ))}
                </div>

                <p className="text-sm text-stone-500">
                  Remove the conflicting session first if you want to add this one instead.
                </p>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={closeConflictWarning}
                className="px-6 py-2 bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Early Bird Password Modal */}
      {showEarlyBirdModal && (
        <EarlyBirdPasswordModal
          eventId={event.id}
          eventName={event.name}
          onSuccess={() => {
            setShowEarlyBirdModal(false);
            navigate(`/events/${event.id}/register`);
          }}
          onCancel={() => setShowEarlyBirdModal(false)}
        />
      )}

      {/* Session Detail Modal */}
      {detailSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setDetailSession(null)}>
          <div className="fixed inset-0 bg-black/40" />
          <div
            className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4 flex items-start justify-between rounded-t-xl">
              <div className="pr-4">
                <h3 className="text-lg font-semibold text-stone-900">{detailSession.title}</h3>
                <p className="text-sm text-stone-500 mt-0.5">
                  {formatTime(detailSession.start_time)} - {formatTime(detailSession.end_time)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDetailSession(null)}
                className="p-1.5 text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5 space-y-4">
              {/* Meta badges */}
              <div className="flex flex-wrap gap-2">
                {detailSession.tags && detailSession.tags.length > 0 ? (
                  detailSession.tags.map(tag => (
                    <span
                      key={tag.id}
                      className="px-2.5 py-1 text-xs rounded-full font-medium"
                      style={{
                        backgroundColor: tag.type === 'licensure' && tag.color ? `${tag.color}20` : '#f5f5f4',
                        color: tag.type === 'licensure' && tag.color ? tag.color : '#78716c',
                        marginRight: '0.25rem',
                      }}
                    >
                      {tag.name}
                    </span>
                  ))
                ) : detailSession.track ? (
                  <span
                    className="px-2.5 py-1 text-xs rounded-full font-medium"
                    style={{
                      backgroundColor: detailSession.track_color ? `${detailSession.track_color}20` : '#f5f5f4',
                      color: detailSession.track_color || '#78716c',
                    }}
                  >
                    {detailSession.track}
                  </span>
                ) : null}
                {detailSession.session_type && (
                  <span className="px-2.5 py-1 text-xs rounded-full bg-stone-100 text-stone-600 font-medium capitalize">
                    {detailSession.session_type.replace('_', ' ')}
                  </span>
                )}
                {detailSession.capacity && (
                  <span className={`px-2.5 py-1 text-xs rounded-full font-medium ${
                    detailSession.current_attendees >= detailSession.capacity
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-stone-100 text-stone-600'
                  }`}>
                    {detailSession.current_attendees}/{detailSession.capacity} seats
                  </span>
                )}
              </div>

              {/* Room */}
              {(detailSession.room_name || detailSession.room) && (
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <svg className="w-4 h-4 text-stone-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {detailSession.room_name
                    ? detailSession.room_building
                      ? `${detailSession.room_building} - ${detailSession.room_name}`
                      : detailSession.room_name
                    : detailSession.room}
                </div>
              )}

              {/* Speakers */}
              {detailSession.speakers?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-stone-700 mb-2">
                    {detailSession.speakers.length === 1 ? 'Speaker' : 'Speakers'}
                  </h4>
                  <div className="space-y-2">
                    {detailSession.speakers.map((sp, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-indigo-600">
                            {(sp.speaker_name || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-stone-800">{sp.speaker_name}</p>
                          {sp.speaker_title && (
                            <p className="text-xs text-stone-500">{sp.speaker_title}{sp.speaker_company ? `, ${sp.speaker_company}` : ''}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {detailSession.description && (
                <div>
                  <h4 className="text-sm font-medium text-stone-700 mb-1">Description</h4>
                  <p className="text-sm text-stone-600 whitespace-pre-wrap">{detailSession.description}</p>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="sticky bottom-0 bg-white border-t border-stone-100 px-6 py-4 rounded-b-xl">
              <button
                type="button"
                onClick={() => setDetailSession(null)}
                className="w-full px-4 py-2 bg-stone-100 text-stone-700 text-sm font-medium rounded-lg hover:bg-stone-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDetail;
